import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { OpenInNew } from '@mui/icons-material'
import { Button } from '@mui/material'

import { useGetTrackedGameLiveEventArchetypesQuery } from '../../../../../api/core'
import { GRTooltip } from '../../../../../components/GRTooltip/GRTooltip'
import { useGetTrackedGameEventsQuery } from '../../../../api/internal'
import { actionButtonSx } from '../helpers/actionButtonSx'
import { customerBlueprintUrl } from './customerLiveEventsUrls'

type CustomerBlueprintLinkProps = {
  gameId: string
  archetypeId: string
}

/**
 * Action button linking from the internal Market Level Blueprints overview to the
 * customer-facing Live Events Tracker.
 *
 * The customer blueprint modal has no blueprint id URL param; it needs a representative
 * event (for the archetype lookup) and its event type id. The blueprint's event ids come
 * from the archetype evolution endpoint (the same query BlueprintEventCount makes, so it
 * is served from the RTK Query cache) and the internal game events list provides the
 * event type id, as the internal list endpoint does not populate archetype data.
 * When no representative event can be resolved (e.g. a blueprint without events or an
 * unpublished game) the link falls back to the plain customer calendar for the game.
 */
export const CustomerBlueprintLink: FC<CustomerBlueprintLinkProps> = ({ gameId, archetypeId }) => {
  const { t } = useTranslation()
  const { data: archetypeEvolution } = useGetTrackedGameLiveEventArchetypesQuery({ archetypeId, type: 'all' })
  const { data: gameEvents } = useGetTrackedGameEventsQuery(gameId)

  const representativeEvent = useMemo(() => {
    if (!archetypeEvolution || !gameEvents) return undefined

    const eventTypeIdByEventId = gameEvents.reduce<{ [eventId: string]: string }>((acc, event) => {
      if (event.id && event.typeId) {
        acc[event.id] = event.typeId
      }
      return acc
    }, {})

    const entry = archetypeEvolution.evolution.find((evolutionEntry) => !!eventTypeIdByEventId[evolutionEntry.eventId])
    return entry ? { eventId: entry.eventId, typeId: eventTypeIdByEventId[entry.eventId] } : undefined
  }, [archetypeEvolution, gameEvents])

  const calendarUrl = customerBlueprintUrl({ gameId })
  const url = representativeEvent ? customerBlueprintUrl({ gameId, eventId: representativeEvent.eventId, typeId: representativeEvent.typeId }) : calendarUrl

  return (
    <GRTooltip content={representativeEvent ? t('internal-live-events:open_customer_blueprint') : t('internal-live-events:open_customer_calendar')}>
      <Button component={Link} to={url} target="_blank" color="primary" variant="contained" sx={actionButtonSx} disableElevation>
        <OpenInNew fontSize="small" />
      </Button>
    </GRTooltip>
  )
}
